import {
  Bleed,
  Box,
  BoxProps,
  HStack,
  Separator,
  Stack,
  Text,
} from "@chakra-ui/react";
import * as React from "react";

export interface DataTableContext<T> {
  id?: keyof T;
  index: number;
  value: T;
}

export interface DataTableColumn<T> {
  id?: keyof T;
  name?: string;
  icon?: React.ReactElement;
  width: number;
  cell?: (ctx: DataTableContext<T>) => React.ReactNode;
}

export interface DataTableProps<T> extends Omit<BoxProps, "columns"> {
  data: T[];
  columns: DataTableColumn<T>[];
}

export default function DataTable<T>({
  columns,
  data,
  ...rootProps
}: DataTableProps<T>) {
  const ref = React.useRef(null);
  const [width, setWidth] = React.useState<number>(0);
  const columnWidthsSum = React.useMemo(
    () => columns.reduce((acc, c) => acc + c.width, 0),
    [columns],
  );
  const relativeColumnWidths = React.useMemo(
    () => columns.map((c) => (c.width / columnWidthsSum) * width),
    [columns, columnWidthsSum, width],
  );
  const getColumnWidth = React.useCallback(
    (idx: number) => {
      const w = relativeColumnWidths[idx];
      if (w === 0) {
        return "auto";
      }
      return `${w}px`;
    },
    [relativeColumnWidths],
  );
  React.useLayoutEffect(() => {
    setWidth(ref.current.getBoundingClientRect().width - 80);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    function onResize() {
      setWidth(0);
      requestAnimationFrame(() => {
        setWidth(ref.current.getBoundingClientRect().width - 80);
      });
    }
  }, []);

  return (
    <Box
      bg="bg.muted"
      borderRadius="md"
      borderWidth="1"
      {...(rootProps as any)}
    >
      <Stack bg="bg" borderBottomWidth="1" borderRadius="md" p="2">
        <HStack bg="gray.50" borderRadius="md" p="2" ref={ref} w="full">
          {columns.map((c, idx) => (
            <Box key={idx} w={getColumnWidth(idx) as any}>
              {c.name && (
                <HStack fontSize="sm">
                  {c.icon} <Text color="fg.muted">{c.name}</Text>
                </HStack>
              )}
            </Box>
          ))}
        </HStack>
        {data.map((d, idx) => (
          <React.Fragment key={idx}>
            <HStack
              color="fg.muted"
              fontSize="sm"
              pl="2"
              pr="2"
              w="full"
              py="2"
            >
              {columns.map((c, idx) => (
                <Box key={idx} w={getColumnWidth(idx) as any}>
                  {c.cell
                    ? c.cell({ id: c.id, index: idx, value: d })
                    : c.id && <Text>{String(d[c.id])}</Text>}
                </Box>
              ))}
            </HStack>
            {idx < data.length - 1 && (
              <Bleed inline="2">
                <Separator />
              </Bleed>
            )}
          </React.Fragment>
        ))}
      </Stack>
      <Box py="4"></Box>
    </Box>
  );
}
